
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../app/hooks/useAuthStore';
import { security } from '../shared/lib/auth/security';
import { notificationService } from '../shared/lib/notification/notification';
import { dataStorageService } from '../shared/services/dataStorageService';
import { appConstants } from '../shared/constants/appConstants';
import { BsEye, BsEyeSlash } from 'react-icons/bs';

const LoginPage = () => {
    const [loginInput, setLoginInput] = useState('');
    const [passwordInput, setPasswordInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    
    // We use useState to trigger UI updates for the attempt count
    // but the value is updated synchronously during submission.
    const [failedCount, setFailedCount] = useState(0);
    
    // Manifest is cached during the session
    const cachedManifestRef = useRef(null);
    
    const { login } = useAuthStore();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!loginInput || !passwordInput) {
            notificationService.warning('Please enter both login and password');
            return;
        }

        setIsLoading(true);

        // Penalty delay calculation: 
        // 1st error (2nd attempt): 0s
        // 2nd error (3rd attempt): 0s
        // 3rd error (4th attempt): 5s
        // 4th error (5th attempt): 10s...
        const delayInSeconds = failedCount >= 3 ? (failedCount - 2) * 5 : 0;
        
        if (delayInSeconds > 0) {
            setCountdown(delayInSeconds);
            const timer = setInterval(() => {
                setCountdown(prev => Math.max(0, prev - 1));
            }, 1000);
            
            await new Promise(r => setTimeout(r, delayInSeconds * 1000));
            clearInterval(timer);
            setCountdown(0);
        }

        try {
            const hashA = security.getHashA(loginInput, passwordInput);
            const hashB = security.getHashB(passwordInput);

            let manifest = cachedManifestRef.current;

            // Only fetch from server if we don't have a cache or if it's one of the first 3 attempts
            if (failedCount < 3 || !manifest) {
                const response = await fetch(appConstants.apiManifestUrl, { cache: 'no-cache', mode: 'cors' });
                if (response.ok) {
                    manifest = await response.json();
                    cachedManifestRef.current = manifest;
                } else {
                    notificationService.error('Connection error. Server is unreachable.');
                    setIsLoading(false);
                    return;
                }
            } else {
                console.log('Validating login using locally cached manifest...');
            }

            const userConfigPath = manifest.user_configs ? manifest.user_configs[hashA] : null;

            if (!userConfigPath) {
                notificationService.error('Incorrect login or password.');
                setFailedCount(prev => prev + 1); // Increase attempt counter
                setIsLoading(false);
                return;
            }

            // Real login: verify that the user's config file exists
            const configUrl = appConstants.apiBaseUrl + userConfigPath;
            console.log('Fetching user config from:', configUrl);
            const configResponse = await fetch(configUrl, { cache: 'no-cache', mode: 'cors' });
            
            if (!configResponse.ok) {
                notificationService.error('Authorization failed: User config file missing.');
                setIsLoading(false);
                return;
            }
            
            // Login Success
            await dataStorageService.clearData();
            localStorage.removeItem(appConstants.localStorageMetaPrefix + 'versions');

            login(hashA, hashB);
            notificationService.success('Welcome back!');
            navigate('/test');
        } catch (err) {
            console.error('Critical Auth Error:', err);
            notificationService.error('A network error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '80vh',
            padding: '20px'
        }}>
            <h1>Authorization</h1>
            {countdown > 0 && (
                <div style={{ color: 'orange', marginBottom: '15px', textAlign: 'center' }}>
                    Hold on! Delay active: {countdown}s
                </div>
            )}
            <form onSubmit={handleSubmit} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '15px', 
                width: '100%', 
                maxWidth: '300px' 
            }}>
                <input
                    type="text"
                    placeholder="Login"
                    value={loginInput}
                    onChange={(e) => setLoginInput(e.target.value)}
                    disabled={isLoading}
                    style={{ padding: '10px', fontSize: '16px' }}
                />
                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        disabled={isLoading}
                        style={{ 
                            padding: '10px', 
                            fontSize: '16px', 
                            width: '100%',
                            boxSizing: 'border-box',
                            paddingRight: '40px'
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#666'
                        }}
                    >
                        {showPassword ? <BsEyeSlash size={20} /> : <BsEye size={20} />}
                    </button>
                </div>
                <button 
                    type="submit" 
                    disabled={isLoading}
                    style={{ 
                        padding: '10px', 
                        fontSize: '16px', 
                        backgroundColor: isLoading ? '#ccc' : '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: isLoading ? 'wait' : 'pointer'
                    }}
                >
                    {isLoading ? (countdown > 0 ? `Waiting... (${countdown}s)` : 'Checking...') : 'Login'}
                </button>
            </form>
            {failedCount > 0 && !isLoading && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                    Failed attempts: {failedCount} (Session only)
                </p>
            )}
        </div>
    );
};

export { LoginPage };
